"use client";

import Image from "next/image";
import { useState } from "react";
import { Play } from "lucide-react";
import { DEMO_VIDEO_ID, DEMO_VIDEO_TITLE } from "./demoVideo";

// Click-to-play YouTube facade for the homepage demo fold.
//
// The real YouTube iframe pulls ~1 MB of player script and several third-party
// requests the moment it mounts, which would land on every homepage visit
// whether or not anyone watches. So the server renders only a poster (the
// video's own thumbnail through next/image) with a play button, and the iframe
// is created on the first click with autoplay on. The poster and the iframe
// share one aspect-video box, so the swap never changes the section height.
//
// youtube-nocookie.com is the privacy-enhanced embed host: no tracking cookies
// are set until the visitor actually plays the video.

const EMBED_URL = `https://www.youtube-nocookie.com/embed/${DEMO_VIDEO_ID}?autoplay=1&rel=0`;
const POSTER_URL = `https://i.ytimg.com/vi/${DEMO_VIDEO_ID}/maxresdefault.jpg`;

export default function LandingDemoVideo() {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-2xl border border-slate-200/60 dark:border-slate-700/60 bg-slate-900 shadow-xl">
      {playing ? (
        <iframe
          src={EMBED_URL}
          title={DEMO_VIDEO_TITLE}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 h-full w-full"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${DEMO_VIDEO_TITLE}`}
          className="group absolute inset-0 h-full w-full cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-[#d47723]/60"
        >
          <Image
            src={POSTER_URL}
            alt=""
            fill
            sizes="(max-width: 1024px) 100vw, 896px"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          />
          {/* The thumbnail carries its own title art, so no caption is drawn
              over it; a light, even tint on hover is the only overlay. */}
          <div className="absolute inset-0 bg-slate-900/0 transition-colors duration-300 group-hover:bg-slate-900/20" />

          {/* Play button */}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-[#d47723] text-white shadow-lg shadow-orange-500/30 ring-4 ring-white/20 transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-1 h-7 w-7 sm:h-9 sm:w-9" fill="currentColor" />
            </span>
          </span>
        </button>
      )}
    </div>
  );
}
